// En lille QR-koder til rumlinket (byte-tilstand, fejlrettelse M, version 1-10). Skrevet her for
// at slippe for en afhængighed; test/ui/qr.test.ts afkoder resultatet igen og tjekker kendte vektorer.

import { BRAND } from '../brand';

/** Fejlrettelse M pr. version: [EC-ord pr. blok, [antal blokke, dataord pr. blok][]]. */
const ECC_M: readonly (readonly [number, readonly (readonly [number, number])[]])[] = [
  [10, [[1, 16]]],
  [16, [[1, 28]]],
  [26, [[1, 44]]],
  [18, [[2, 32]]],
  [24, [[2, 43]]],
  [16, [[4, 27]]],
  [18, [[4, 31]]],
  [22, [[2, 38], [2, 39]]],
  [22, [[3, 36], [2, 37]]],
  [26, [[4, 43], [1, 44]]],
];

const ALIGN: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

// ---------------------------------------------------------------------------
// Reed-Solomon over GF(256) med polynomiet 0x11D

function gfMul(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

function rsDivisor(degree: number): number[] {
  const r = new Array<number>(degree).fill(0);
  r[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      r[j] = gfMul(r[j] as number, root);
      if (j + 1 < degree) r[j] = (r[j] as number) ^ (r[j + 1] as number);
    }
    root = gfMul(root, 0x02);
  }
  return r;
}

/** Fejlrettelsesord til en blok data. */
export function rsEcc(data: readonly number[], degree: number): number[] {
  const div = rsDivisor(degree);
  const r = new Array<number>(degree).fill(0);
  for (const b of data) {
    const f = b ^ (r.shift() as number);
    r.push(0);
    for (let i = 0; i < degree; i++) r[i] = (r[i] as number) ^ gfMul(div[i] as number, f);
  }
  return r;
}

// ---------------------------------------------------------------------------
// Matrix

export interface QrCode {
  version: number;
  size: number;
  mask: number;
  /** modules[y][x]: true er mørk. */
  modules: boolean[][];
}

function dataCapacity(v: number): number {
  const [, groups] = ECC_M[v - 1] as (typeof ECC_M)[number];
  return groups.reduce((s, [n, k]) => s + n * k, 0);
}

function utf8(s: string): number[] {
  return Array.from(new TextEncoder().encode(s));
}

function bch(value: number, poly: number, polyBits: number, dataBits: number): number {
  let r = value << (polyBits - 1);
  for (let i = dataBits + polyBits - 2; i >= polyBits - 1; i--) if ((r >>> i) & 1) r ^= poly << (i - polyBits + 1);
  return (value << (polyBits - 1)) | r;
}

/** De 15 formatbit for fejlrettelse M (00) og en maske. */
export function formatBits(mask: number): number {
  return bch((0 << 3) | mask, 0x537, 11, 5) ^ 0x5412;
}

function versionBits(v: number): number {
  return bch(v, 0x1f25, 13, 6);
}

const MASKS: readonly ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

class Grid {
  m: boolean[][];
  fn: boolean[][];
  constructor(readonly size: number) {
    this.m = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
    this.fn = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  }
  set(x: number, y: number, dark: boolean): void {
    if (x < 0 || y < 0 || x >= this.size || y >= this.size) return;
    (this.m[y] as boolean[])[x] = dark;
    (this.fn[y] as boolean[])[x] = true;
  }
}

function drawFunction(g: Grid, v: number): void {
  const n = g.size;
  for (let i = 0; i < n; i++) {
    g.set(6, i, i % 2 === 0);
    g.set(i, 6, i % 2 === 0);
  }
  const finder = (cx: number, cy: number): void => {
    for (let dy = -4; dy <= 4; dy++)
      for (let dx = -4; dx <= 4; dx++) {
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        g.set(cx + dx, cy + dy, d !== 2 && d !== 4);
      }
  };
  finder(3, 3);
  finder(n - 4, 3);
  finder(3, n - 4);
  const al = ALIGN[v - 1] as readonly number[];
  for (let i = 0; i < al.length; i++)
    for (let j = 0; j < al.length; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === al.length - 1) || (i === al.length - 1 && j === 0)) continue;
      const cx = al[i] as number;
      const cy = al[j] as number;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) g.set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  drawFormat(g, 0);
  if (v >= 7) {
    const bits = versionBits(v);
    for (let i = 0; i < 18; i++) {
      const bit = ((bits >>> i) & 1) === 1;
      const a = n - 11 + (i % 3);
      const b = Math.floor(i / 3);
      g.set(a, b, bit);
      g.set(b, a, bit);
    }
  }
}

function drawFormat(g: Grid, mask: number): void {
  const n = g.size;
  const bits = formatBits(mask);
  const bit = (i: number): boolean => ((bits >>> i) & 1) === 1;
  for (let i = 0; i <= 5; i++) g.set(8, i, bit(i));
  g.set(8, 7, bit(6));
  g.set(8, 8, bit(7));
  g.set(7, 8, bit(8));
  for (let i = 9; i < 15; i++) g.set(14 - i, 8, bit(i));
  for (let i = 0; i < 8; i++) g.set(n - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i++) g.set(8, n - 15 + i, bit(i));
  g.set(8, n - 8, true);
}

function placeData(g: Grid, data: readonly number[]): void {
  const n = g.size;
  let i = 0;
  for (let right = n - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < n; vert++)
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const up = ((right + 1) & 2) === 0;
        const y = up ? n - 1 - vert : vert;
        if (!(g.fn[y] as boolean[])[x] && i < data.length * 8) {
          (g.m[y] as boolean[])[x] = (((data[i >>> 3] as number) >>> (7 - (i & 7))) & 1) === 1;
          i++;
        }
      }
  }
}

function applyMask(g: Grid, mask: number): void {
  const f = MASKS[mask] as (x: number, y: number) => boolean;
  for (let y = 0; y < g.size; y++)
    for (let x = 0; x < g.size; x++) if (!(g.fn[y] as boolean[])[x] && f(x, y)) (g.m[y] as boolean[])[x] = !(g.m[y] as boolean[])[x];
}

/** Strafpoint efter standarden: lange rækker, 2x2-felter, findermønstre og balance. */
function penalty(m: boolean[][]): number {
  const n = m.length;
  let p = 0;
  const at = (x: number, y: number, col: boolean): boolean => (col ? (m[x] as boolean[])[y] : (m[y] as boolean[])[x]) as boolean;
  for (const col of [false, true]) {
    for (let y = 0; y < n; y++) {
      let run = 1;
      for (let x = 1; x < n; x++) {
        if (at(x, y, col) === at(x - 1, y, col)) {
          run++;
          if (run === 5) p += 3;
          else if (run > 5) p++;
        } else run = 1;
      }
      // 1:1:3:1:1 med fire lyse på den ene side.
      for (let x = 0; x + 10 < n + 4; x++) {
        const get = (k: number): boolean => (k < 0 || k >= n ? false : at(k, y, col));
        const core = get(x) && !get(x + 1) && get(x + 2) && get(x + 3) && get(x + 4) && !get(x + 5) && get(x + 6);
        if (!core) continue;
        const before = !get(x - 1) && !get(x - 2) && !get(x - 3) && !get(x - 4);
        const after = !get(x + 7) && !get(x + 8) && !get(x + 9) && !get(x + 10);
        if (before || after) p += 40;
      }
    }
  }
  let dark = 0;
  for (let y = 0; y < n; y++)
    for (let x = 0; x < n; x++) {
      const c = (m[y] as boolean[])[x] as boolean;
      if (c) dark++;
      if (x + 1 < n && y + 1 < n && c === (m[y] as boolean[])[x + 1] && c === (m[y + 1] as boolean[])[x] && c === (m[y + 1] as boolean[])[x + 1]) p += 3;
    }
  const k = Math.ceil(Math.abs(dark * 20 - n * n * 10) / (n * n)) - 1;
  return p + Math.max(0, k) * 10;
}

/** Lav en QR-kode til teksten. Kaster, hvis den ikke kan være i version 10. */
export function qrEncode(text: string): QrCode {
  const bytes = utf8(text);
  let v = 1;
  for (; v <= 10; v++) {
    const countBits = v <= 9 ? 8 : 16;
    if (4 + countBits + bytes.length * 8 <= dataCapacity(v) * 8) break;
  }
  if (v > 10) throw new Error('For lang tekst til QR');
  const cap = dataCapacity(v) * 8;
  const bits: number[] = [];
  const put = (val: number, len: number): void => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1);
  };
  put(0b0100, 4);
  put(bytes.length, v <= 9 ? 8 : 16);
  for (const b of bytes) put(b, 8);
  put(0, Math.min(4, cap - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);
  for (let pad = 0xec; bits.length < cap; pad ^= 0xec ^ 0x11) put(pad, 8);
  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | (bits[i + j] as number);
    data.push(b);
  }
  // Del i blokke, læg fejlrettelse på, og flet.
  const [ec, groups] = ECC_M[v - 1] as (typeof ECC_M)[number];
  const blocks: number[][] = [];
  const ecs: number[][] = [];
  let k = 0;
  for (const [count, len] of groups)
    for (let c = 0; c < count; c++) {
      const blk = data.slice(k, k + len);
      k += len;
      blocks.push(blk);
      ecs.push(rsEcc(blk, ec));
    }
  const all: number[] = [];
  const maxLen = Math.max(...blocks.map((b) => b.length));
  for (let i = 0; i < maxLen; i++) for (const b of blocks) if (i < b.length) all.push(b[i] as number);
  for (let i = 0; i < ec; i++) for (const e of ecs) all.push(e[i] as number);
  const size = v * 4 + 17;
  let best: Grid | null = null;
  let bestMask = 0;
  let bestP = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const g = new Grid(size);
    drawFunction(g, v);
    placeData(g, all);
    applyMask(g, mask);
    drawFormat(g, mask);
    const p = penalty(g.m);
    if (p < bestP) {
      bestP = p;
      best = g;
      bestMask = mask;
    }
  }
  const g = best as Grid;
  return { version: v, size, mask: bestMask, modules: g.m };
}

/** SVG med fire moduler stille zone. Mørk er blæk på hvid, som alle telefoner kan læse. */
export function qrSvg(text: string, label = 'QR-kode'): string {
  const q = qrEncode(text);
  const n = q.size + 8;
  let d = '';
  for (let y = 0; y < q.size; y++) {
    let x = 0;
    while (x < q.size) {
      if (!(q.modules[y] as boolean[])[x]) {
        x++;
        continue;
      }
      let w = 1;
      while (x + w < q.size && (q.modules[y] as boolean[])[x + w]) w++;
      d += `M${x + 4} ${y + 4}h${w}v1h-${w}z`;
      x += w;
    }
  }
  const safe = label.replace(/[<>&"]/g, '');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" role="img" aria-label="${safe}" shape-rendering="crispEdges"><rect width="${n}" height="${n}" fill="${BRAND.white}"/><path d="${d}" fill="${BRAND.ink}"/></svg>`;
}
